import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import aiSupportService, { AIChatResponse, AISuggestedAction } from '../services/aiSupportService';
import { isNativeAppRuntime } from '../utils/checkoutReturn';
import './AIChatWidget.css';

/* ================================================================
   Rotating bunny support agents
   ================================================================ */
const SUPPORT_AGENTS = [
  { firstName: 'Jessica', avatar: '/assets/ai-support/agent-jessica.jpg' },
  { firstName: 'Juliet',  avatar: '/assets/ai-support/agent-juliet.jpg' },
  { firstName: 'Daisy',   avatar: '/assets/ai-support/agent-daisy.jpg' },
  { firstName: 'Bella',   avatar: '/assets/ai-support/agent-bella.jpg' },
  { firstName: 'Luna',    avatar: '/assets/ai-support/agent-luna.jpg' },
  { firstName: 'Rosie',   avatar: '/assets/ai-support/agent-rosie.jpg' },
] as const;
const LAST_NAME = 'Rabbit';

let chatMessageIdSequence = 0;

function makeChatMessageId(prefix: string): string {
  chatMessageIdSequence += 1;
  return `${prefix}-${Date.now()}-${chatMessageIdSequence}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ================================================================
   Types
   ================================================================ */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  confidence?: number;
}

type CallState = 'idle' | 'connecting' | 'active' | 'ended';

/* ================================================================
   Singleton Retell client (one per page load)
   ================================================================ */
let retellClient: RetellWebClient | null = null;
function getRetellClient(): RetellWebClient {
  if (!retellClient) retellClient = new RetellWebClient();
  return retellClient;
}

/* ================================================================
   Component
   ================================================================ */

const AIChatWidget: React.FC = () => {
  const isNativeAppView = isNativeAppRuntime();

  // ── Chat state ───────────────────────────────
  const [agentIndex, setAgentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<AISuggestedAction[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  // ── Current bunny agent (rotates on each open) ───────────────
  const agent = SUPPORT_AGENTS[agentIndex];
  const agentFullName = `${agent.firstName} ${LAST_NAME}`;

  // ── Escalation / call state ──────────────────
  const [, setEscalationCount] = useState(0);
  const [showCallOption, setShowCallOption] = useState(false);
  const [callState, setCallState] = useState<CallState>('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [callError, setCallError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [agentSpeaking, setAgentSpeaking] = useState(false);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeCallIdRef = useRef<string | null>(null);
  const callDurationRef = useRef(0);
  const callEndingRef = useRef(false);

  // ── Mobile detection ─────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Drag state ────────────────────────────────
  // pos: pixel offset from bottom-right corner (matching CSS default)
  const [pos, setPos] = useState<{ right: number; bottom: number }>({ right: 24, bottom: 24 });
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; startRight: number; startBottom: number }>({
    active: false, startX: 0, startY: 0, startRight: 24, startBottom: 24,
  });
  const onDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = { active: true, startX: clientX, startY: clientY, startRight: pos.right, startBottom: pos.bottom };
    // NOTE: do not call e.preventDefault() here — touch listeners are passive
  }, [pos]);

  const onDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!dragRef.current.active) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const dx = dragRef.current.startX - clientX;
    const dy = dragRef.current.startY - clientY;
    const newRight = Math.max(8, Math.min(window.innerWidth - 66, dragRef.current.startRight + dx));
    // Keep bottom low enough so the window header (≈50px) never goes above the page nav (≈70px)
    const maxBottom = window.innerHeight - 640 - 70; // 640 = approx window height, 70 = nav
    const newBottom = Math.max(8, Math.min(Math.max(8, maxBottom), dragRef.current.startBottom + dy));
    setPos({ right: newRight, bottom: newBottom });
  }, []);

  const onDragEnd = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, [onDragMove, onDragEnd]);

  // ── Refs ──────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const openChat = useCallback(() => {
    setAgentIndex((i) => (i + 1) % SUPPORT_AGENTS.length);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('bid4babes:open-support-chat', openChat);
    window.addEventListener('openSupportChat', openChat);
    return () => {
      window.removeEventListener('bid4babes:open-support-chat', openChat);
      window.removeEventListener('openSupportChat', openChat);
    };
  }, [openChat]);

  // ── Auto-scroll ──────────────────────────────
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    callDurationRef.current = callDuration;
  }, [callDuration]);

  // ── Focus input on open ──────────────────────
  useEffect(() => {
    if (isOpen && hasStarted && callState === 'idle') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, hasStarted, callState]);

  // ── Cleanup on unmount ───────────────────────
  useEffect(() => {
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      try { retellClient?.stopCall(); } catch (_) { /* ignore */ }
    };
  }, []);

  /* ────────────────────────────────────────────────
     Chat logic
     ──────────────────────────────────────────────── */

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: makeChatMessageId('user'),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setSuggestedActions([]);
    setHasStarted(true);

    try {
      const response: AIChatResponse = await aiSupportService.chat({
        message: text.trim(),
        session_id: sessionId,
        context: { page: window.location.pathname, agent_name: agentFullName },
      });

      setSessionId(response.session_id);

      const aiMsg: ChatMessage = {
        id: makeChatMessageId('ai'),
        role: 'assistant',
        content: response.message.content,
        timestamp: new Date(),
        confidence: response.message.ai_confidence ?? undefined,
      };

      setMessages((prev) => [...prev, aiMsg]);

      if (response.suggested_actions) {
        setSuggestedActions(response.suggested_actions);
      }

      const isLowConfidence = (response.message.ai_confidence ?? 1) < 0.55;
      const isEscalated = response.needs_escalation;

      if (isEscalated || isLowConfidence) {
        setEscalationCount((c) => {
          const next = c + 1;
          if (next >= 2) setShowCallOption(true);
          return next;
        });
      }

      if (isEscalated) {
        setShowCallOption(true);
        const sysMsg: ChatMessage = {
          id: makeChatMessageId('sys'),
          role: 'system',
          content: "I've flagged this for our specialist team and created a support ticket. You can also switch to a voice call for faster resolution.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, sysMsg]);
      }

      if (response.resolved) {
        setTimeout(() => setShowFeedback(true), 2000);
      }
    } catch (error) {
      console.error('Support chat error:', error);
      const errorMsg: ChatMessage = {
        id: makeChatMessageId('error'),
        role: 'system',
        content: "I'm having trouble connecting right now. Please try again or call us directly.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleSuggestionClick = (action: AISuggestedAction) => sendMessage(action.message);
  const handleQuickStart = (message: string) => sendMessage(message);

  /* ────────────────────────────────────────────────
     Voice call logic — Retell Web SDK
     ──────────────────────────────────────────────── */

  const startCall = async () => {
    setCallState('connecting');
    setCallError(null);
    setCallDuration(0);
    callDurationRef.current = 0;
    callEndingRef.current = false;
    setIsMuted(false);
    setAgentSpeaking(false);

    try {
      const callData = await aiSupportService.createWebCall(agentFullName);

      if (!callData?.access_token) {
        throw new Error('Voice service is currently unavailable. Please try chat support.');
      }

      // Store call_id for post-call action processing
      activeCallIdRef.current = callData.call_id || null;

      const client = getRetellClient();
      (client as any).removeAllListeners?.('call_started');
      (client as any).removeAllListeners?.('call_ended');
      (client as any).removeAllListeners?.('error');
      (client as any).removeAllListeners?.('agent_start_talking');
      (client as any).removeAllListeners?.('agent_stop_talking');

      // Wire up Retell event handlers
      client.on('call_started', () => {
        setCallState('active');
        callTimerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
      });

      client.on('call_ended', () => {
        _handleCallEnded();
      });

      client.on('error', (err) => {
        console.error('Retell call error:', err);
        setCallError('Call connection lost. Returning to chat.');
        _handleCallEnded();
      });

      client.on('agent_start_talking', () => setAgentSpeaking(true));
      client.on('agent_stop_talking', () => setAgentSpeaking(false));

      await client.startCall({ accessToken: callData.access_token });
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Voice support is currently unavailable. Please continue via chat.';
      setCallError(msg);
      setCallState('idle');
    }
  };

  const _handleCallEnded = () => {
    if (callEndingRef.current) return;
    callEndingRef.current = true;
    const endedDuration = callDurationRef.current;

    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallState('ended');
    setAgentSpeaking(false);

    // Notify backend to process post-call actions (password reset, tickets, etc.)
    const callId = activeCallIdRef.current;
    if (callId) {
      aiSupportService.notifyCallEnded(callId);
      activeCallIdRef.current = null;
    }

    setTimeout(() => {
      setCallState('idle');
      const content = `Call with ${agentFullName} ended (${formatDuration(endedDuration)}). Feel free to continue chatting here if you need anything else.`;
      setMessages((prev) => [
        ...prev,
        {
          id: makeChatMessageId('sys-call-end'),
          role: 'system',
          content,
          timestamp: new Date(),
        },
      ]);
      callEndingRef.current = false;
    }, 1500);
  };

  const endCall = () => {
    try { getRetellClient().stopCall(); } catch (_) { /* ignore */ }
    _handleCallEnded();
  };

  const toggleMute = () => {
    try {
      const client = getRetellClient();
      if (isMuted) {
        client.unmute();
      } else {
        client.mute();
      }
      setIsMuted((m) => !m);
    } catch (_) { /* ignore if SDK not connected */ }
  };

  const backToChat = () => {
    if (callState === 'active') endCall();
    else setCallState('idle');
  };

  /* ────────────────────────────────────────────────
     Feedback
     ──────────────────────────────────────────────── */

  const submitFeedback = async () => {
    if (!sessionId || feedbackRating === 0) return;
    try {
      await aiSupportService.submitFeedback({ session_id: sessionId, satisfaction: feedbackRating });
    } catch (e) {
      console.error('Feedback error:', e);
    }
    setShowFeedback(false);
  };

  const createManualTicket = async () => {
    if (isLoading) return;
    sendMessage('Please create a support ticket for my current issue.');
    setShowCallOption(true);
  };

  /* ────────────────────────────────────────────────
     Close / reset
     ──────────────────────────────────────────────── */

  const closeChat = async () => {
    if (callState === 'active') endCall();
    if (sessionId) {
      try { await aiSupportService.closeChat(sessionId); } catch (_) { /* ignore */ }
    }
    setIsOpen(false);
  };

  const resetChat = () => {
    if (callState === 'active') endCall();
    setMessages([]);
    setSessionId(null);
    setHasStarted(false);
    setSuggestedActions([]);
    setShowFeedback(false);
    setFeedbackRating(0);
    setShowCallOption(false);
    setEscalationCount(0);
    setCallState('idle');
    callEndingRef.current = false;
    callDurationRef.current = 0;
    setCallDuration(0);
    setCallError(null);
    setIsMuted(false);
    setAgentSpeaking(false);
  };

  /* ────────────────────────────────────────────────
     Helpers
     ──────────────────────────────────────────────── */

  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatContent = (text: string) => {
    let f = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    f = f.replace(/^- (.*)/gm, '• $1');
    f = f.replace(/^(\d+)\. /gm, '<span style="color:#a5b4fc;font-weight:500">$1.</span> ');
    f = f.replace(/✅/g, '<span style="color:#4ade80">✅</span>');
    f = f.replace(/\n/g, '<br/>');
    return f;
  };

  /* ────────────────────────────────────────────────
     Render
     ──────────────────────────────────────────────── */

  return (
    <>
      {/* ═══ FAB ═════════════════════════════════ */}
      {!isOpen && !isNativeAppView && (
        <button
          className="ai-chat-fab"
          style={{ right: pos.right, bottom: pos.bottom }}
          onClick={openChat}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          aria-label={`Contact Customer Support Bunnies`}
          id="ai-chat-fab"
          title="Drag to move • Click to open support"
        >
          <img className="ai-chat-fab-avatar" src={agent.avatar} alt={`${agent.firstName} support`} />
          <span className="ai-chat-fab-badge" />
        </button>
      )}

      {/* ═══ Chat Window ═════════════════════════ */}
      {isOpen && (
        <div
          className={`ai-chat-window ${isNativeAppView ? 'ai-chat-window--app' : ''}`}
          id="ai-chat-window"
          role="dialog"
          aria-label={`Customer Support Bunnies`}
          style={{
            right: isMobile ? undefined : pos.right,
            bottom: isMobile ? undefined : pos.bottom + 68,
            maxHeight: isMobile ? undefined : `calc(100dvh - ${pos.bottom + 68 + 70}px)`,
          }}
        >

          {/* ── Header (drag handle) ────────────── */}
          <div
            className="ai-chat-header ai-chat-header--draggable"
            onMouseDown={onDragStart}
            onTouchStart={onDragStart}
            title="Drag to move"
          >
            <div className="ai-chat-header-avatar" style={{ fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <img className="ai-agent-avatar-image" src={agent.avatar} alt={agent.firstName} />
            </div>
            <div className="ai-chat-header-info">
              <h3 className="ai-chat-header-title">Customer Support Bunnies</h3>
              <div className="ai-chat-header-status">
                {callState === 'active' ? 'On voice call' : `${agentFullName} • Online now`}
              </div>
            </div>
            <div className="ai-chat-header-actions">
              {callState === 'idle' && (
                <>
                  <button className="ai-chat-header-btn main-action" onClick={startCall} title="Start Voice Call" aria-label="Start voice call">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.1 6.1l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </button>
                  <button className="ai-chat-header-btn main-action" onClick={createManualTicket} title="Create Support Ticket" aria-label="Create support ticket">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                      <line x1="12" y1="18" x2="12" y2="12" />
                      <line x1="9" y1="15" x2="15" y2="15" />
                    </svg>
                  </button>
                </>
              )}
              <button className="ai-chat-header-btn" onClick={resetChat} title="New Conversation" aria-label="New conversation">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 .49-4.5" />
                </svg>
              </button>
              <button className="ai-chat-header-btn" onClick={closeChat} title="Close" aria-label="Close support">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* ── Content ─────────────────────────── */}
          <div className="ai-chat-content">

            {/* Feedback overlay */}
            {showFeedback && (
              <div className="ai-feedback-overlay">
                <div className="ai-feedback-icon">⭐</div>
                <h4>How did we do?</h4>
                <p>Rate your experience with {agentFullName}</p>
                <div className="ai-feedback-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`ai-feedback-star ${feedbackRating >= star ? 'active' : ''}`}
                      onClick={() => setFeedbackRating(star)}
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                {feedbackRating > 0 && (
                  <p className="ai-feedback-label">
                    {feedbackRating === 5 ? 'Excellent!' : feedbackRating === 4 ? 'Great!' : feedbackRating === 3 ? 'Good' : feedbackRating === 2 ? 'Fair' : 'Poor'}
                  </p>
                )}
                <button className="ai-feedback-submit" onClick={submitFeedback} disabled={feedbackRating === 0}>
                  Submit Feedback
                </button>
                <button className="ai-feedback-skip" onClick={() => setShowFeedback(false)}>
                  Skip
                </button>
              </div>
            )}

            {/* ── Voice Call Overlay ── */}
            {callState !== 'idle' && (
              <div className="ai-call-overlay">
                <div className={`ai-call-glow ${callState === 'active' ? 'active' : 'connecting'}`} />

                {/* Agent avatar */}
                <div className={`ai-call-circle ${callState === 'active' ? 'active' : 'connecting'}`}>
                  {callState === 'active' && (
                    <>
                      <div className={`ai-call-ring ${agentSpeaking ? 'speaking' : ''}`} />
                      <div className={`ai-call-ring ${agentSpeaking ? 'speaking' : ''}`} />
                    </>
                  )}
                  <div className="ai-call-agent-icon">
                    {callState === 'connecting' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.1 6.1l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    ) : callState === 'ended' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    )}
                  </div>
                  {callState === 'active' && (
                    <span className="ai-call-duration">{formatDuration(callDuration)}</span>
                  )}
                  {callState === 'connecting' && (
                    <span className="ai-call-status-text">Connecting…</span>
                  )}
                  {callState === 'ended' && (
                    <span className="ai-call-status-text">Call ended</span>
                  )}
                </div>

                <div className="ai-call-info">
                  <span className="ai-call-label">
                    {callState === 'connecting'
                      ? `Connecting to ${agentFullName}…`
                      : callState === 'active'
                      ? agentSpeaking ? `${agentFullName} is speaking…` : `${agentFullName}`
                      : `Thanks for calling, ${agent.firstName}!`}
                  </span>
                  <span className="ai-call-sublabel">
                    {callState === 'active'
                      ? 'Speak naturally — your agent is listening'
                      : callState === 'connecting'
                      ? 'Please wait a moment'
                      : 'Returning to chat…'}
                  </span>
                </div>

                {/* Audio visualizer bars */}
                {callState === 'active' && (
                  <div className={`ai-call-bars ${agentSpeaking ? 'speaking' : ''}`}>
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <div key={i} className="ai-call-bar" />
                    ))}
                  </div>
                )}

                {/* Call controls */}
                {callState === 'active' && (
                  <div className="ai-call-controls">
                    <button
                      className={`ai-call-control-btn ${isMuted ? 'muted' : ''}`}
                      onClick={toggleMute}
                      title={isMuted ? 'Unmute' : 'Mute'}
                      aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                    >
                      {isMuted ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="1" y1="1" x2="23" y2="23" />
                          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                      )}
                      <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                    </button>
                    <button className="ai-call-end-btn" onClick={endCall} title="End Call" aria-label="End call">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                      </svg>
                      <span>End Call</span>
                    </button>
                  </div>
                )}

                {callState !== 'ended' && callState !== 'active' && (
                  <button className="ai-call-back-btn" onClick={backToChat} aria-label="Return to chat">
                    ← Back to chat
                  </button>
                )}

                {callError && (
                  <div className="ai-call-error">
                    {callError}
                  </div>
                )}
              </div>
            )}

            {/* ── Welcome Screen or Messages ── */}
            {callState === 'idle' && (
              <>
                {!hasStarted ? (
                  <div className="ai-chat-welcome">
                    <div className="ai-chat-welcome-icon" style={{ background: 'none', border: 'none', boxShadow: 'none', width: 72, height: 72, overflow: 'hidden' }}>
                      <img className="ai-agent-avatar-image" src={agent.avatar} alt={agent.firstName} />
                    </div>
                    <h3>Hi, I'm {agentFullName} 👋</h3>
                    <p className="ai-chat-welcome-subtitle">Customer Support Bunnies</p>
                    <p className="ai-chat-welcome-desc">
                      I'm here to help with your account, wallet, Game-a-Rama, deliveries, and anything else. What can I assist you with today?
                    </p>
                    <div className="ai-chat-welcome-actions">
                      <button className="ai-chat-welcome-action" onClick={() => handleQuickStart("What's my current balance?")}>
                        <span className="ai-welcome-action-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                          </svg>
                        </span>
                        <span>Check my balance</span>
                      </button>
                      <button className="ai-chat-welcome-action" onClick={() => handleQuickStart('Where is my delivery?')}>
                        <span className="ai-welcome-action-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                          </svg>
                        </span>
                        <span>Track my delivery</span>
                      </button>
                      <button className="ai-chat-welcome-action" onClick={() => handleQuickStart("I can't log in to my account")}>
                        <span className="ai-welcome-action-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </span>
                        <span>Login / account help</span>
                      </button>
                      <button className="ai-chat-welcome-action" onClick={() => handleQuickStart('Show me my Game-a-Rama activity')}>
                        <span className="ai-welcome-action-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                          </svg>
                        </span>
                        <span>My Game-a-Rama &amp; entries</span>
                      </button>
                    </div>
                    <div className="ai-chat-welcome-footer">
                      <button className="ai-chat-welcome-call-btn" onClick={startCall} aria-label="Start voice call">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.1 6.1l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        Prefer a voice call?
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ai-chat-messages">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`ai-msg ${msg.role}`}>
                        {msg.role !== 'user' && (
                          <div className="ai-msg-avatar" style={{ background: 'none', boxShadow: 'none', border: 'none', overflow: 'hidden' }}>
                            {msg.role === 'assistant' ? (
                              <img className="ai-agent-avatar-image" src={agent.avatar} alt={agent.firstName} />
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                            )}
                          </div>
                        )}
                        <div className="ai-msg-body">
                          {msg.role === 'assistant' && (
                            <div className="ai-msg-sender">{agentFullName} · Customer Support Bunnies</div>
                          )}
                          <div
                            className="ai-msg-bubble"
                            dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
                          />
                          <div className="ai-msg-time">{formatTime(msg.timestamp)}</div>
                        </div>
                        {msg.role === 'user' && (
                          <div className="ai-msg-avatar user-avatar">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Typing indicator */}
                    {isLoading && (
                      <div className="ai-msg assistant">
                        <div className="ai-msg-avatar" style={{ background: 'none', boxShadow: 'none', border: 'none', overflow: 'hidden' }}>
                          <img className="ai-agent-avatar-image" src={agent.avatar} alt={agent.firstName} />
                        </div>
                        <div className="ai-msg-body">
                          <div className="ai-msg-sender">{agentFullName} is typing…</div>
                          <div className="ai-typing-dots">
                            <div className="ai-typing-dot" />
                            <div className="ai-typing-dot" />
                            <div className="ai-typing-dot" />
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Suggested Actions ─────────────── */}
          {callState === 'idle' && suggestedActions.length > 0 && (
            <div className="ai-chat-suggestions">
              {suggestedActions.map((action, i) => (
                <button
                  key={i}
                  className="ai-chat-suggestion-btn"
                  onClick={() => handleSuggestionClick(action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Escalation / Call CTA Banner ───── */}
          {callState === 'idle' && showCallOption && hasStarted && (
            <div className="ai-escalation-banner">
              <div className="ai-escalation-banner-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="ai-escalation-banner-text">
                <h5>Need more help?</h5>
                <p>Switch to voice or open a ticket</p>
              </div>
              <div className="ai-escalation-banner-actions">
                <button className="ai-escalation-call-btn" onClick={startCall}>
                  Call Now
                </button>
                <button
                  className="ai-escalation-ticket-btn"
                  onClick={() => sendMessage('Please create a support ticket for my issue.')}
                >
                  Open Ticket
                </button>
              </div>
            </div>
          )}

          {/* ── Input Area ───────────────────── */}
          {callState === 'idle' && (
            <div className="ai-chat-input-area">
              <div className="ai-chat-input-wrapper">
                <textarea
                  ref={inputRef}
                  className="ai-chat-input"
                  placeholder="Type your message…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isLoading}
                  id="ai-chat-input"
                  aria-label="Type your message"
                />
              </div>
              <button
                className="ai-chat-send-btn"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                title="Send message"
                id="ai-chat-send-btn"
                aria-label="Send message"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
